import { useCallback, useEffect, useRef } from "react";
import {
  SegmentProduct,
  SEGMENT_LIST_FORMAT,
  SEGMENT_LIST_ID,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import { ContextKey } from "src/utils/product-list-context-keys";
import { useIsLoggedIn } from "./useIsLoggedIn";
import { useFeatureFlags } from "./useFeatureFlags";
import { usePageViewedContext } from "./context/use-page-viewed-context";
import { trackProductListViewedEvent } from "src/tracking/events/product-list-viewed";

export interface useProductListViewedData {
  defaultSortBy?: string;
  listFormat: SEGMENT_LIST_FORMAT;
  listId: SEGMENT_LIST_ID | string;
  listType: SEGMENT_LIST_TYPE | ContextKey;
  numberOfItems: number;
  shopType?: string;
  query?: string;
  resultId?: string;
}

export interface UseProductListViewed {
  addProductToQueue: (product: SegmentProduct) => void;
}

interface IProductListViewedQueue {
  addItemToQueue: (product: SegmentProduct) => void;
  clearQueue: () => SegmentProduct[];
  getQueue: () => SegmentProduct[];
  setCallback: (cb: (products: SegmentProduct[]) => void) => void;
}

class ProductListViewedQueue implements IProductListViewedQueue {
  private limit: number;
  private queue: SegmentProduct[] = [];
  private callback: (products: SegmentProduct[]) => void;

  constructor(cb: (products: SegmentProduct[]) => void, limit = 12) {
    this.callback = cb;
    this.limit = limit;
  }

  setCallback(cb: (products: SegmentProduct[]) => void) {
    this.callback = cb;
  }

  getQueue() {
    return this.queue;
  }

  clearQueue() {
    const q = [...this.queue];
    this.queue = [];
    return q;
  }

  addItemToQueue(product: SegmentProduct) {
    this.queue.push(product);
    if (this.queue.length === this.limit) {
      this.callback([...this.queue]);
      this.queue = [];
    }
  }
}

const useProductListViewed = ({
  defaultSortBy,
  listFormat,
  listId,
  listType,
  shopType,
  numberOfItems,
  resultId,
  query,
}: useProductListViewedData): UseProductListViewed => {
  const isLoggedIn = useIsLoggedIn();
  const { enableGatedHomepage } = useFeatureFlags();
  const pageViewTracked = usePageViewedContext();

  const sendDataToSegment = useCallback(
    async (products: SegmentProduct[]) => {
      if (products.length === 0) {
        return;
      }

      return await trackProductListViewedEvent({
        resultId,
        defaultSortBy,
        productListMeta: {
          listFormat,
          listId,
          listType,
          shopType,
        },
        numberOfItems,
        products,
        query,
        gated: !!(enableGatedHomepage && !isLoggedIn),
      });
    },
    [
      resultId,
      defaultSortBy,
      listFormat,
      listId,
      listType,
      shopType,
      numberOfItems,
      query,
      enableGatedHomepage,
      isLoggedIn,
    ]
  );

  const queue = useRef(
    new ProductListViewedQueue((products: SegmentProduct[]) => {
      void sendDataToSegment(products);
    }, 100)
  );

  const dispatchData = useRef(async () => {
    await sendDataToSegment(queue.current.getQueue());
  });

  useEffect(() => {
    queue.current.setCallback((products) => {
      void sendDataToSegment(products);
    });

    dispatchData.current = async () => {
      await sendDataToSegment(queue.current.getQueue());
    };
  }, [sendDataToSegment]);

  useEffect(() => {
    if (resultId && pageViewTracked) {
      void (async () => {
        await sendDataToSegment(queue.current.clearQueue());
      })();
    }
  }, [resultId, pageViewTracked, sendDataToSegment]);

  useEffect(() => {
    if (query && pageViewTracked) {
      void (async () => {
        await sendDataToSegment(queue.current.clearQueue());
      })();
    }
  }, [query, pageViewTracked, sendDataToSegment]);

  useEffect(() => {
    if (!pageViewTracked) {
      return;
    }

    void dispatchData.current();
  }, [pageViewTracked]);

  useEffect(() => {
    window.addEventListener("beforeunload", () => void dispatchData.current());
    return () => {
      window.removeEventListener(
        "beforeunload",
        () => void dispatchData.current()
      );
    };
  }, []);

  const addProductToQueue = useCallback((product: SegmentProduct) => {
    queue.current.addItemToQueue(product);
  }, []);

  return {
    addProductToQueue,
  };
};

export default useProductListViewed;
